import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { TvLogin } from './TvLogin';
import { TvProfiles } from './TvProfiles';
import { TvSubRequired, hasTvSubAccess } from './TvSubRequired';
import { TvProfileTransition } from './TvProfileTransition';
import { TvHome } from './TvHome';
import { TvSearch } from './TvSearch';
import { TvSettings } from './TvSettings';
import { TvDetail } from './TvDetail';
import { TvScreensaver } from './TvScreensaver';
import { TvExitConfirm } from './TvExitConfirm';
import { LOGO_URL, logTv, isNativeTvWrapper, exitTvApp } from './tvAssets';

// Garde-fou : si l'interface TV crashe (moteur TV ancien), on log l'erreur
// et on recharge automatiquement au lieu de rester sur un écran violet figé
export class TvErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) {
    try {
      sessionStorage.setItem('tvMode', '1');
      logTv('react_crash', `${err && err.message} || ${String((info && info.componentStack) || '').slice(0, 300)}`);
    } catch { /* ignore */ }
    setTimeout(() => { try { window.location.href = '/tv'; } catch { /* ignore */ } }, 3000);
  }
  render() {
    if (this.state.err) {
      return (
        <div style={{ width: '100vw', height: '100vh', background: '#0b0612', color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }} data-testid="tv-crash-recovery">
          <img src={LOGO_URL} alt="Maya+" style={{ height: '4vw', marginBottom: '2vw' }} />
          <p style={{ fontSize: '1.6vw', fontWeight: 700 }}>Rechargement en cours…</p>
        </div>
      );
    }
    return this.props.children;
  }
}
import './tv.css';

const IDLE_MS = 2 * 60 * 1000;

export default function TvApp() {
  const { user, loading, activeProfile, setActiveProfile } = useAuth();
  const [detail, setDetail] = useState(null);
  const [view, setView] = useState('home');
  const [pTrans, setPTrans] = useState(null); // profile-select transition {profile, fromRect}
  const [saver, setSaver] = useState(false);
  const [exitAsk, setExitAsk] = useState(false); // Fire TV : dialogue « Quitter Maya+ ? » sur Retour à la racine
  const askExit = useCallback(() => setExitAsk(true), []);

  // Remote disconnect watchdog: if this device was disconnected from another screen, go back to login
  useEffect(() => {
    if (!user) return;
    const t = setInterval(() => {
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { withCredentials: true }).catch(err => {
        if (err.response?.status === 401) {
          try { sessionStorage.removeItem('activeProfile'); } catch { /* ignore */ }
          window.location.reload();
        }
      });
    }, 60000);
    return () => clearInterval(t);
  }, [user]);
  // Returning from playback: skip intro, keep profile, reopen the content detail page
  const resume = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem('tv_resume') || 'null'); } catch { return null; }
  }, []);
  const [intro, setIntro] = useState(!resume);
  const [introFade, setIntroFade] = useState(false);
  const introFadeRef = useRef(false);
  const introKillRef = useRef(null);
  const endIntro = useCallback(() => {
    if (introFadeRef.current) return;
    introFadeRef.current = true;
    setIntroFade(true);
    setTimeout(() => setIntro(false), 750);
  }, []);
  const saverRef = useRef(false);
  useEffect(() => { saverRef.current = saver; }, [saver]);

  // Intro video: NOT skippable — keys are swallowed while it plays.
  // Le timeout de sécurité ne coupe l'intro QUE si la lecture n'a jamais démarré ;
  // une fois la vidéo lancée, elle joue jusqu'au bout (onEnded) puis fondu enchaîné vers les profils.
  useEffect(() => {
    if (!intro) return;
    introKillRef.current = setTimeout(endIntro, 9000);
    // Fire TV (exigence Amazon) : Retour pendant l'intro = quitter l'app immédiatement
    const onKey = (e) => {
      e.preventDefault(); e.stopImmediatePropagation();
      if (isNativeTvWrapper && (e.key === 'Escape' || e.keyCode === 27 || e.keyCode === 8 || e.keyCode === 461 || e.keyCode === 10009)) {
        logTv('firetv_exit_during_intro', {});
        exitTvApp();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => { clearTimeout(introKillRef.current); window.removeEventListener('keydown', onKey, true); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intro]);

  // Screensaver: slideshow after a few minutes of inactivity
  useEffect(() => {
    let t;
    const arm = () => { clearTimeout(t); t = setTimeout(() => setSaver(true), IDLE_MS); };
    const onKey = (e) => {
      if (saverRef.current) { e.preventDefault(); e.stopImmediatePropagation(); setSaver(false); }
      arm();
    };
    // Trailer playback counts as activity: keep resetting the idle timer while a trailer plays,
    // so the countdown only starts once the trailer has finished
    const onMediaActivity = (e) => {
      if (saverRef.current) return;
      if (e.target && e.target.tagName === 'VIDEO') arm();
    };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('mousemove', arm);
    window.addEventListener('timeupdate', onMediaActivity, true);
    arm();
    return () => { clearTimeout(t); window.removeEventListener('keydown', onKey, true); window.removeEventListener('mousemove', arm); window.removeEventListener('timeupdate', onMediaActivity, true); };
  }, []);

  // Stop any playing trailer (audio + video) the moment the screensaver kicks in
  useEffect(() => {
    if (!saver) return;
    document.querySelectorAll('[data-testid="tv-detail-trailer"], [data-testid="tv-card-trailer"], [data-testid="tv-hero-trailer"]').forEach(v => {
      try { v.pause(); v.muted = true; v.dispatchEvent(new Event('ended')); } catch { /* ignore */ }
    });
  }, [saver]);

  // Always land on the profile selection screen when entering the TV app (except when returning from playback)
  useEffect(() => {
    if (resume) {
      sessionStorage.removeItem('tv_resume');
      // home:true → retour direct sur l'accueil des vignettes (intro sautée, fiche non rouverte)
      if (resume.id && !resume.home) setDetail(resume);
    } else {
      setActiveProfile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    sessionStorage.setItem('tvMode', '1');
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Piège d'historique : sur l'app LG installée (disableBackHistoryAPI:false), webOS déclenche
  // un history.back() système à CHAQUE appui Retour — à la racine il quitterait l'app.
  // On garde toujours une entrée sentinelle : le Retour ne quitte jamais l'app (quitter = bouton Accueil TV).
  useEffect(() => {
    const arm = () => { try { window.history.pushState({ tvGuard: true }, '', window.location.href); } catch { /* ignore */ } };
    arm();
    logTv('tvapp_mount', { resume: resume?.id || null, home: !!resume?.home });
    const backTs = { t: 0 };
    const rec = (e) => { if (e.isTrusted && [27, 8, 461, 10009].indexOf(e.keyCode) !== -1) backTs.t = Date.now(); };
    const onPop = () => {
      logTv('popstate', window.location.pathname);
      setTimeout(arm, 0);
      // Retour système webOS sans keydown 461 délivré → on rejoue la touche Retour
      // pour que l'UI réagisse (fermer la fiche, la recherche, etc.)
      if (Date.now() - backTs.t < 700) return;
      logTv('pop_synth_back_tvapp', {});
      const ev = new KeyboardEvent('keydown', { key: 'Escape', bubbles: true, cancelable: true });
      try { Object.defineProperty(ev, 'keyCode', { get: () => 461 }); } catch { /* ignore */ }
      window.dispatchEvent(ev);
    };
    window.addEventListener('keydown', rec, true);
    window.addEventListener('popstate', onPop);
    return () => { window.removeEventListener('keydown', rec, true); window.removeEventListener('popstate', onPop); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = useCallback((item, trailerAt) => setDetail({ ...item, _trailerAt: typeof trailerAt === 'number' ? trailerAt : null }), []);
  const closeDetail = useCallback(() => setDetail(null), []);

  let screen;
  if (loading) {
    screen = <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <img src={LOGO_URL} alt="Maya+" style={{ height: '3.5vw' }} />
    </div>;
  } else if (!user) {
    screen = <TvLogin />;
  } else if (!hasTvSubAccess(user)) {
    screen = <TvSubRequired />;
  } else if (!activeProfile) {
    screen = <TvProfiles onSelect={(p, rect) => setPTrans({ profile: p, fromRect: rect })} onExitRequest={askExit} active={!exitAsk} />;
  } else if (view === 'search') {
    screen = <TvSearch onClose={() => setView('home')} onOpenDetail={openDetail} active={!detail && !exitAsk} />;
  } else if (view === 'settings') {
    screen = <TvSettings onClose={() => setView('home')} />;
  } else {
    screen = <TvHome onOpenDetail={openDetail} onOpenSearch={() => setView('search')} onOpenSettings={() => setView('settings')} active={!detail && !saver && !exitAsk} onExitRequest={askExit} />;
  }

  return (
    <div className="tv-root" data-testid="tv-root">
      {/* Screens mount only after the launch video so entrance animations are visible ;
          pendant le fondu de l'intro, les profils sont déjà montés dessous (fondu enchaîné) */}
      {(!intro || introFade) && screen}
      {pTrans && <TvProfileTransition profile={pTrans.profile} fromRect={pTrans.fromRect}
        onCommit={() => setActiveProfile(pTrans.profile)} onDone={() => setPTrans(null)} />}
      {detail && <TvDetail item={detail} onClose={closeDetail} />}
      {exitAsk && <TvExitConfirm onCancel={() => setExitAsk(false)} />}
      {saver && user && <TvScreensaver />}
      {intro && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 60, background: '#000',
          opacity: introFade ? 0 : 1, transition: 'opacity 0.7s ease' }} data-testid="tv-intro">
          <video autoPlay playsInline
            onPlaying={() => {
              // la lecture a démarré : on annule le kill de secours, la vidéo va au bout
              clearTimeout(introKillRef.current);
              introKillRef.current = setTimeout(endIntro, 30000);
            }}
            ref={el => {
              if (!el || el.dataset.boot) return;
              el.dataset.boot = '1';
              el.muted = false;
              el.play().catch(() => {
                // Browser blocks audible autoplay: start muted, switch to sound as soon as allowed
                el.muted = true;
                el.play().catch(() => { /* safety timeout will close */ });
                const iv = setInterval(() => {
                  if (!el.isConnected) { clearInterval(iv); return; }
                  if (navigator.userActivation && navigator.userActivation.hasBeenActive) {
                    el.muted = false;
                    clearInterval(iv);
                  }
                }, 200);
                const onFirstInput = () => {
                  window.removeEventListener('pointerdown', onFirstInput, true);
                  if (!el.isConnected) return;
                  el.muted = false;
                  if (el.currentTime < 3.5) el.currentTime = 0;
                  el.play().catch(() => { /* ignore */ });
                };
                window.addEventListener('pointerdown', onFirstInput, true);
              });
            }}
            onEnded={endIntro}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}>
            <source src="/intro/maya-intro.mp4" type="video/mp4" />
            <source src="/intro/maya-intro.webm" type="video/webm" />
          </video>
        </div>
      )}
    </div>
  );
}
